import React, { useState, useEffect, useRef, useContext, useImperativeHandle } from "react";
import classNames from "classnames/bind";
import { withRouter, Link } from "react-router-dom";

import Field from "core/ui/forms/field";
import DropZone from "core/ui/drop-zone";
import SessionContext from "SessionContext";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Card, CardBody } from "reactstrap";
import RequiredLocationFields from "../partials/required-location-fields";
import recolor_svg from "../core/utils/recolor_svg";
import { Buffer } from "buffer";
import isEqual from "lodash.isequal";
import LocationRebuildNotice from "../core/ui/location-rebuild-notice";

const ContentEditable = React.forwardRef((props, ref) => {
  const inputRef = useRef(null);
  const valueRef = useRef(props.value || "");

  useImperativeHandle(ref, () => inputRef.current, []);

  useEffect(() => {
    if (props.value !== valueRef.current) {
      valueRef.current = props.value;
      requestAnimationFrame(() => {
        if (inputRef.current) {
          inputRef.current.innerText = props.value;
          inputRef.current.title = props.title;
        }
      });
    }
  }, [props.value, props.title]);

  function onChange() {
    const val = inputRef.current.innerText.replace(/\n/g, " ");
    valueRef.current = val;
    inputRef.current.classList.toggle("has-content", !!val);
  }

  function onBlur() {
    props.onChange && props.onChange({ target: { value: valueRef.current } });
  }

  return (
    <span
      className={classNames("content-editable-input", {
        placeholder: !props.value,
        "has-content": !!valueRef.current,
      })}
      title={props.title}
      contentEditable
      onInput={onChange}
      onBlur={onBlur}
      ref={inputRef}
    />
  );
});

const DataTypeSelector = (props) => {
  return (
    <div className="data-type-selector">
      <select
        title="Data Type"
        onChange={props.onChange}
        value={props.value || "text"}
      >
        {props.type &&
          props.data_types[props.type].map((opt) => (
            <option key={opt}>{opt}</option>
          ))}
        {!props.type && (
          <>
            <option value="text">Text / Unspecified</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="uid">Unique ID</option>
            {Object.entries(props.data_types).map(([key, opts]) => (
              <optgroup label={key} key={key}>
                {opts.map((opt) => (
                  <option key={opt}>{opt}</option>
                ))}
              </optgroup>
            ))}
          </>
        )}
      </select>
    </div>
  );
};

const CalculatedField = ({ allow_constants = false, mapping, type, data_types, onColDragStart, onColDragEnd, onChange, setColType }) => {
  const draggingI = useRef(-1);
  const focusNextRender = useRef(false);
  const firstEditableRef = useRef(null);

  useEffect(() => {
    if (onChange && allow_constants && allow_constants.length > 0) {
      onChange([{ type: "constant", value: allow_constants[0] }]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function condenseEntries(entries) {
    const seed = [[], {}];
    const reduced = entries.reduce(([condensed, last_entry], entry) => {
      if (entry.type === "text" && last_entry.type === "text") {
        last_entry.value = (last_entry.value + " " + entry.value).trim();
        entry = last_entry;
      } else {
        entry = Object.assign({}, entry);
        condensed.push(entry);
      }
      return [condensed, entry];
    }, seed)[0];
    if (reduced.length === 1 && reduced[0].type === "text" && reduced[0].value === "") {
      return [];
    }
    return reduced;
  }

  function onDragStart(col, ev) {
    draggingI.current = mapping.indexOf(col);
    onColDragStart([col.column], "move", ev);
  }

  function onDragEnd(ev) {
    if (draggingI.current > -1 && ev.dataTransfer.dropEffect !== "none") {
      onRemove(draggingI.current);
    }
    draggingI.current = -1;
    onColDragEnd(ev);
  }

  function onDrop(i, ev) {
    const newMapping = mapping.slice();
    const cols = JSON.parse(ev.dataTransfer.getData("text/plain"));
    for (const col of cols) {
      const remove_i = newMapping.findIndex((m) => m.type === "column" && m.column === col);
      if (remove_i > -1) {
        newMapping.splice(remove_i, 1);
        if (i > remove_i) i--;
      }
      newMapping.splice(i, 0, { type: "column", column: col }, { type: "text", value: "" });
      i += 2;
    }
    newMapping.unshift({ type: "text", value: "" });
    onChange(condenseEntries(newMapping));
    draggingI.current = -1;
  }

  function onRemove(i) {
    const newMapping = mapping.slice();
    newMapping.splice(i, 1);
    onChange(condenseEntries(newMapping));
  }

  function setTextValue(i, ev) {
    const newEntries = mapping.slice();
    newEntries[i] = { type: "text", value: ev.target.value };
    onChange && onChange(condenseEntries(newEntries));
  }

  function addTextEntry() {
    onChange && onChange([{ type: "text", value: "" }]);
    focusNextRender.current = true;
  }

  function handleSetConstantValue(ev) {
    onChange && onChange([{ type: "constant", value: ev.target.value }]);
  }

  function handleSetColType(col, ev) {
    setColType(col, ev.target.value);
  }

  const entries = mapping;
  if (focusNextRender.current) {
    focusNextRender.current = false;
    requestAnimationFrame(() => {
      firstEditableRef.current && firstEditableRef.current.focus();
    });
  }
  const use_select = allow_constants && allow_constants.length > 0;

  return (
    <div className="calc-field">
      {use_select && (!entries.length || entries[0].type === "constant") && (
        <select value={entries.length && entries[0].value} onChange={handleSetConstantValue}>
          {allow_constants.map((c) => (
            <option key={c.id}>{c.name}</option>
          ))}
        </select>
      )}

      {(!use_select || (entries.length > 0 && entries[0].type === "column")) &&
        entries.length > 0 &&
        entries.map((entry, i) => (
          <React.Fragment key={i}>
            <DropZone onDrop={(ev) => onDrop(i, ev)} />

            {entry.type === "text" && (
              <ContentEditable
                value={entry.value}
                onChange={(ev) => setTextValue(i, ev)}
                title={entry.value || "Add Text"}
                ref={i === 0 ? firstEditableRef : undefined}
              />
            )}

            {entry.type === "column" && (
              <span
                className="column"
                draggable={true}
                onDragStart={(ev) => onDragStart(entry, ev)}
                onDragEnd={onDragEnd}
              >
                <span className="text">
                  {entry.column}
                  <button className="actions-btn tools remove" onClick={() => onRemove(i)}>
                    &times;
                  </button>
                </span>
                <DataTypeSelector
                  type={type}
                  data_types={data_types}
                  onChange={(ev) => handleSetColType(entry.column, ev)}
                />
              </span>
            )}
          </React.Fragment>
        ))}

      {!use_select && (
        <DropZone onDrop={(ev) => onDrop(entries.length, ev)}>
          {allow_constants === true && entries.length === 0 && (
            <span className="placeholder faux-editable" onClick={addTextEntry}>
              Drop columns or enter text here
            </span>
          )}
          {allow_constants === false && entries.length === 0 && (
            <span className="placeholder">Drop columns here</span>
          )}
        </DropZone>
      )}
    </div>
  );
};

const MatchKeyField = ({ cols, typings, type, data_types, onColDragStart, onColDragEnd, onChange, setColType }) => {
  const draggingI = useRef(-1);

  function onDragStart(col, ev) {
    draggingI.current = cols.indexOf(col);
    onColDragStart([col], "move", ev);
  }

  function onDragEnd(ev) {
    if (draggingI.current > -1 && ev.dataTransfer.dropEffect !== "none") {
      onRemove(draggingI.current);
    }
    draggingI.current = -1;
    onColDragEnd(ev);
  }

  function onDrop(i, ev) {
    const newCols = cols.slice();
    const droppedCols = JSON.parse(ev.dataTransfer.getData("text/plain"));
    for (const col of droppedCols) {
      const remove_i = newCols.indexOf(col);
      if (remove_i > -1) {
        newCols.splice(remove_i, 1);
        if (i > remove_i) i--;
      }
      newCols.splice(i, 0, col);
      i++;
    }
    onChange(newCols);
    draggingI.current = -1;
  }

  function onRemove(i) {
    const newCols = cols.slice();
    newCols.splice(i, 1);
    onChange(newCols);
  }

  function handleSetColType(col, ev) {
    setColType(col, ev.target.value);
  }

  return (
    <div className="calc-field">
      {cols.length > 0 &&
        cols.map((col, i) => (
          <React.Fragment key={i}>
            <DropZone onDrop={(ev) => onDrop(i, ev)} />
            <span
              className="column"
              draggable={true}
              onDragStart={(ev) => onDragStart(col, ev)}
              onDragEnd={onDragEnd}
            >
              <span className="text">
                {col}
                <button className="actions-btn tools remove" onClick={() => onRemove(i)}>
                  &times;
                </button>
              </span>
              <DataTypeSelector
                type={type}
                data_types={data_types}
                value={typings[col] || "text"}
                onChange={(ev) => handleSetColType(col, ev)}
              />
            </span>
          </React.Fragment>
        ))}

      <DropZone onDrop={(ev) => onDrop(cols.length, ev)}>
        {cols.length === 0 && (
          <span className="placeholder">Drop columns here</span>
        )}
      </DropZone>
    </div>
  );
};

const errorLine = { padding: "5px 0", maxWidth: "90%", lineHeight: "1.5em" };

const INITIAL_DATASETS = [
  {
    id: "standard",
    name: "Locations",
    match_keys: [
      {
        name: "Location Name (this needs to be one field with 'Full Name' chosen)",
        type: "NAD",
        fields: [{ label: "Name", type: "Name" }],
      },
      {
        name: "Address",
        type: "NAD",
        fields: [{ label: "Address", type: "Address" }],
      },
      {
        name: "Coordinates",
        type: "C",
        fields: [{ label: "Coordinates", type: "Coordinates" }],
      },
    ],
    fields: [
      { field: "location_type", label: "Location Type", required: true },
    ],
  },
];

const ProcessLocationUpload = (props) => {
  const context = useContext(SessionContext);
  const [upload, setUpload] = useState(undefined);
  const [droppingEnabled, setDroppingEnabled] = useState(false);
  const [selectedCols, setSelectedCols] = useState(new Set());
  const [columnFilter, setColumnFilter] = useState("");
  const [description, setDescription] = useState("");
  const [typings, setTypings] = useState({});
  const [mappings, setMappings] = useState({});
  const [defaultMappings, setDefaultMappings] = useState({});
  const [dataTypes, setDataTypes] = useState({});
  const [errorModalIsOpen, setErrorModalIsOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [locationTypesLoaded, setLocationTypesLoaded] = useState(false);
  const [showRebuildNotice, setShowRebuildNotice] = useState(false);

  function onCloseRebuildNotice() {
    setShowRebuildNotice(false);
    props.history.push({
      pathname: "/upload-details/" + upload.id,
      state: { refer: "location-uploads" },
    });
  }

  useEffect(() => {
    context.query("uploads/get", { id: props.subpage }).then((uploadData) => {
      setDefaultMappings(uploadData.default_mappings);
      setDataTypes(uploadData.data_types);
      setTypings(uploadData.typings);
      setDescription(uploadData.description || "");
      setMappings(uploadData.mappings || {});
      setUpload(uploadData);
    });

    context
      .query("location/available-location-types", {
        companyId: context.appState.activeCompany.id,
      })
      .then((response) => {
        const locTypes = response.map((entry) => ({
          id: entry.id,
          name: entry.name,
          svg: entry.svg,
          color: entry.color,
        }));
        setLocationTypes(locTypes);
        setLocationTypesLoaded(true);
      })
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function getErrorMessages() {
    let uniques = [];
    if (errorMessages.length) {
      errorMessages.forEach((newValue) => {
        if (!uniques.length) {
          uniques.push(newValue);
        } else {
          let found = false;
          for (let i = 0; i < uniques.length; i++) {
            if (isEqual(newValue, uniques[i])) {
              found = true;
            }
          }
          if (!found) {
            uniques.push(newValue);
          }
        }
      });
    }
    return (
      <ul>
        {uniques.length > 0 ? uniques.map(function (line, i) {
          return (
            <li style={errorLine} key={i}>
              {line}
            </li>
          );
        }) : null}{" "}
      </ul>
    );
  }

  function toggleModal() {
    setErrorModalIsOpen((prev) => !prev);
  }

  function prepDragStart(cols, dropEffect, ev) {
    const dragImg = ev.target.cloneNode(true);
    dragImg.classList.add("column-drag-img");
    if (cols.length > 1) {
      const cnt = document.createElement("span");
      cnt.classList.add("count");
      cnt.innerText = " + " + (cols.length - 1) + " more";
      dragImg.appendChild(cnt);
    }
    ev.dataTransfer.setData("text/plain", JSON.stringify(cols));
    ev.dataTransfer.dropEffect = dropEffect;
    document.body.appendChild(dragImg);
    ev.dataTransfer.setDragImage(dragImg, 0, 0);
    requestAnimationFrame(() => document.body.removeChild(dragImg));
    setDroppingEnabled(true);
  }

  function onColDragStart(header, ev) {
    const cols = new Set(selectedCols);
    cols.add(header);
    prepDragStart(Array.from(cols), "link", ev);
  }

  function cleanupDragEnd() {
    setDroppingEnabled(false);
  }

  function onColDragEnd(ev) {
    if (ev.dataTransfer.dropEffect !== "none") {
      setSelectedCols(new Set());
    }
    cleanupDragEnd();
  }

  function onSetDescription(ev) {
    setDescription(ev.target.value);
  }

  function updateMapping(dataset, group, field, newMappings) {
    setMappings((prevMappings) => {
      const newState = JSON.parse(JSON.stringify(prevMappings));
      newState[dataset] = newState[dataset] || {};
      newState[dataset][group] = newState[dataset][group] || {};
      const oldMappings = newState[dataset][group][field] || [];
      newState[dataset][group][field] = newMappings;
      if (
        group === "match_keys" &&
        typeof newState === "object" &&
        typeof newState[dataset][group][field] === "object"
      ) {
        setTypings((prevTypings) => {
          const newTypings = Object.assign({}, prevTypings);
          oldMappings.forEach((value) => {
            if (!newMappings.includes(value)) {
              delete newTypings[value];
            }
          });
          newState[dataset][group][field].forEach((value) => {
            if (
              typeof newTypings === "object" &&
              !(typeof newTypings[value] === "string")
            ) {
              if (typeof defaultMappings[field][0].description === "string") {
                newTypings[value] = defaultMappings[field][0].description;
              }
            }
          });
          return newTypings;
        });
      }
      return newState;
    });
  }

  function toggleSelectCol(header) {
    setSelectedCols((prev) => {
      const next = new Set(prev);
      if (next.has(header)) {
        next.delete(header);
      } else {
        next.add(header);
      }
      return next;
    });
  }

  function setColType(col, type) {
    setTypings((prev) => Object.assign({}, prev, { [col]: type }));
  }

  function handleColumnFilterChange(ev) {
    setColumnFilter(ev.target.value);
  }

  function getSvgImgSrc(svg) {
    const buff = Buffer.from(svg);
    const base64data = buff.toString("base64");
    return `data:image/svg+xml;base64,${base64data}`;
  }

  function getMappedColumns(mapped_cols) {
    for (const dataset of Object.values(mappings)) {
      const match_key_mappings = Object.values(dataset["match_keys"] || {});
      for (const cols of match_key_mappings) {
        for (const col of cols) {
          mapped_cols.add(col);
        }
      }
      Object.values(dataset["imports"] || {}).forEach((mapping) =>
        mapping
          .filter((m) => m.type === "column")
          .forEach((m) => mapped_cols.add(m.column))
      );
    }
  }

  async function submitJob() {
    let errors = [];

    const getFieldByValue = (value) => {
      let keys = Object.keys(typings).filter((key) => {
        if (typings[key] === value) return key;
        return null;
      });
      return keys.map((key, i) => {
        if (i === keys.length - 1) {
          return <strong key={key}>"{key}"</strong>;
        }
        return <strong key={key}>"{key}", </strong>;
      });
    };

    const checkForDuplicates = () => {
      let fields = Object.values(typings);
      const duplicates = fields.filter((item, index) => fields.indexOf(item) !== index);
      if (duplicates.length) {
        duplicates.forEach((entry) => {
          errors.push(<span><strong>{entry}</strong> has multiple mappings: {getFieldByValue(entry)}</span>);
        });
      }
    };

    if (!description || description.length === 0) {
      errors.push("Description required.");
    }
    if (Object.keys(mappings.standard.match_keys).length === 0) {
      errors.push("Mappings required.");
    }
    if (Object.keys(typings).length === 0) {
      errors.push("Mappings assignments required.");
    } else {
      checkForDuplicates();
    }

    if (errors.length > 0) {
      setErrorMessages(errors);
      toggleModal();
      return;
    }

    const params = {
      upload_id: upload.id,
      description,
      mappings,
      typings,
      location_type_id: undefined,
    };
    if (mappings.standard.imports.location_type[0].value.name) {
      params.location_type_id = locationTypes.find(
        (o) => o.name === mappings.standard.imports.location_type[0].value.name
      ).id;
    } else {
      params.location_type_id = locationTypes.find(
        (o) => o.name === mappings.standard.imports.location_type[0].value
      ).id;
    }

    try {
      await context.command(
        "upload/process_location_upload",
        params,
        {},
        { defaultErrorHandling: false }
      );
    } catch (e) {
      errors = [];
      errors.push(e);
      setErrorMessages(errors);
      toggleModal();
      return;
    }
    setShowRebuildNotice(true);
  }

  const mapped_cols = new Set();
  getMappedColumns(mapped_cols);
  const columnFilter_lower = columnFilter.toLowerCase();

  if (!upload) return <span>Loading</span>;

  const preview = upload.preview || [];
  const headers = upload.content_descriptor.columns.map((c) => c.header);

  return (
    <div className="page process-uploads-page">
      <div className="list-header">
        <Link to="/location-uploads">
          <button type="button" className="new-button">
            <i className="bi bi-chevron-left"></i>{" "} Location Uploads
          </button>
        </Link>
        <h1>Location Details</h1>
        <i className="spacer" />
      </div>

      <section className="mt-4">
        <details open title="Hide/Display File Preview">
          <summary><h2>{"File Preview: "}<span>{upload.filename}</span></h2></summary>
          <Card className="preview">
            <CardBody className="scroll">
              <div className="table-container">
                <table className="styled">
                  <thead>
                    <tr>
                      {headers.map((header, i) => (
                        <th className="file-header" key={i}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview &&
                      preview.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </details>
      </section>

      <div
        className={classNames("process-uploads-content", {
          "dropping-enabled": droppingEnabled,
        })}
      >
        <div
          className={classNames("input-cols-rail", {
            "is-selecting": selectedCols.size > 0,
          })}
        >
          <h1>Map Columns</h1>
          <div className="scroll-content">
            <div className="datasource">
              <h3>{upload.filename}</h3>
              <table className="styled">
                <thead>
                  <tr>
                    <th>Columns</th>
                  </tr>
                  <tr>
                    <th>
                      <input
                        type="search"
                        value={columnFilter}
                        onChange={handleColumnFilterChange}
                        placeholder="filter columns"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {headers
                    .filter((h) => h.toLowerCase().indexOf(columnFilter_lower) > -1)
                    .map((header, i) => {
                      const mapped = mapped_cols.has(header);
                      const selected = selectedCols.has(header);
                      return (
                        <tr key={i}>
                          <td>
                            <span
                              className={classNames("column", {
                                "is-mapped": mapped,
                                "is-selected": selected,
                              })}
                              title={mapped ? header + ", mapped" : header}
                              onClick={() => toggleSelectCol(header)}
                              draggable={true}
                              onDragStart={(ev) => onColDragStart(header, ev)}
                              onDragEnd={onColDragEnd}
                            >
                              <span className="text">{header}</span>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="datasets-rail">
          <h1>Import to Dataset</h1>
          <div>
            <Field label="Description">
              <input
                type="text"
                value={description}
                className="profile-details-info"
                onChange={onSetDescription}
              />
            </Field>
          </div>
          <div>
            <Modal
              centered
              backdrop
              isOpen={errorModalIsOpen}
              toggle={toggleModal}
              className="modal-lg"
            >
              <ModalHeader toggle={toggleModal}>
                Error Importing File
              </ModalHeader>
              <ModalBody>{getErrorMessages()}</ModalBody>
              <ModalFooter>
                <Button onClick={toggleModal}>Close</Button>
              </ModalFooter>
            </Modal>
          </div>

          <div className="scroll-content">
            {INITIAL_DATASETS.map((dataset, i) => (
              <div className="dataset" key={i}>
                <h3>{dataset.name}</h3>
                <table className="styled">
                  {locationTypesLoaded && (
                    <>
                      <thead>
                        <tr>
                          <th>Required Fields</th>
                          <th></th>
                          <th width={1}>Label</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dataset.fields
                          .filter((f) => f.required)
                          .map((entry) => {
                            let field = entry;
                            let loc_type = undefined;
                            if (mappings.standard.imports) {
                              if (mappings.standard.imports.location_type[0].value.name) {
                                loc_type = locationTypes.find(
                                  (o) => o.name === mappings.standard.imports.location_type[0].value.name
                                );
                              } else {
                                loc_type = locationTypes.find(
                                  (o) => o.name === mappings.standard.imports.location_type[0].value
                                );
                              }
                            }
                            let allowed_values = true;
                            if (entry.field) {
                              field = entry.field;
                              allowed_values = locationTypes;
                            }
                            const mapping =
                              ((mappings[dataset.id] || {})["imports"] || {})[field] || [];
                            return (
                              <tr key={field}>
                                <td>
                                  <CalculatedField
                                    mapping={mapping}
                                    data_types={props.data_types}
                                    allow_constants={allowed_values}
                                    onColDragStart={prepDragStart}
                                    onColDragEnd={cleanupDragEnd}
                                    onChange={updateMapping.bind(null, dataset.id, "imports", field)}
                                    setColType={setColType}
                                  />
                                </td>
                                <td>
                                  {locationTypesLoaded && entry.field === "location_type" && loc_type && (
                                    <>
                                      <img
                                        align="center"
                                        height="25"
                                        width="25"
                                        alt="icon"
                                        src={getSvgImgSrc(
                                          recolor_svg(loc_type.svg, loc_type.color, "currentColor")
                                        )}
                                      />
                                      &nbsp;
                                    </>
                                  )}
                                </td>
                                <td>{entry.label}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </>
                  )}

                  {dataset.match_keys.map((match_key, j) => (
                    <React.Fragment key={j}>
                      <thead>
                        <tr>
                          <th>Match Key: {match_key.name}</th>
                          <th></th>
                          <th width={1}>Label</th>
                        </tr>
                      </thead>
                      <tbody>
                        {match_key.fields.map((field, i) => {
                          const cols =
                            ((mappings[dataset.id] || {})["match_keys"] || {})[field.label] || [];
                          return (
                            <tr key={i}>
                              <td>
                                <MatchKeyField
                                  cols={cols}
                                  typings={typings}
                                  type={field.type}
                                  data_types={dataTypes}
                                  onColDragStart={prepDragStart}
                                  onColDragEnd={cleanupDragEnd}
                                  onChange={updateMapping.bind(null, dataset.id, "match_keys", field.label)}
                                  setColType={setColType}
                                />
                              </td>
                              <td></td>
                              <td>{field.label}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </React.Fragment>
                  ))}

                  <tbody>
                    <tr className="spacer">
                      <td colSpan={2} />
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>

      <footer>
        <RequiredLocationFields />
        <span id="columns-mapped">
          {mapped_cols.size} of {headers.length} Columns Mapped
        </span>
        <button onClick={submitJob}>Import</button>
      </footer>
      <LocationRebuildNotice show={showRebuildNotice} onClose={onCloseRebuildNotice} />
    </div>
  );
};

ProcessLocationUpload.site_section = "uploads";

export default withRouter(ProcessLocationUpload);
